import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';

import {
  addLanguage,
  deleteLanguage,
  updateDefaultLanguage,
} from '../../languages/actions';
import { deleteTranslation } from '../../translations/actions';
import { deleteVariable, updateVariable } from '../../variables';
import { updateGreeting } from '../actions';
import { greetingReferencesAdapter } from '../adapters';
import {
  DEFAULT_AGENT_SETTINGS_LANGUAGE_CODE,
  DEFAULT_GREETING,
} from '../constants';
import type { Greeting } from '../types';
import { getAgentSettingsReferenceKey } from '../utils';

export const greetingAdapter = createEntityAdapter<Greeting, string>({
  selectId: greeting =>
    greeting.languageCode ?? DEFAULT_AGENT_SETTINGS_LANGUAGE_CODE,
});

const initialState = greetingAdapter.getInitialState();

const slice = createSlice({
  name: 'greeting',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateGreeting, (state, action) => {
      const existingGreeting = state.entities[action.payload.languageCode];

      if (existingGreeting) {
        assignDefinedFields(existingGreeting, action.payload);
        assignMetadata(action, existingGreeting);
        greetingAdapter.updateOne(state, {
          id: action.payload.languageCode,
          changes: existingGreeting,
        });
      } else {
        // Create a new greeting if it doesn't exist
        const newGreeting: Greeting = {
          ...DEFAULT_GREETING,
          welcomeMessage:
            action.payload.welcomeMessage ?? DEFAULT_GREETING.welcomeMessage,
          languageCode: action.payload.languageCode,
        };
        assignDefinedFields(newGreeting, action.payload);
        assignMetadata(action, newGreeting);
        greetingAdapter.addOne(state, newGreeting);
      }
    });

    // Add a new greeting when a new language is added
    builder.addCase(addLanguage, (state, action) => {
      const languageCode = action.payload.code;
      const newGreeting: Greeting = {
        ...DEFAULT_GREETING,
        languageCode,
      };
      // update greeting if existing
      const existingGreeting = state.entities[languageCode];
      if (existingGreeting) {
        assignDefinedFields(existingGreeting, newGreeting);
        assignMetadata(action, existingGreeting);
        greetingAdapter.updateOne(state, {
          id: languageCode,
          changes: existingGreeting,
        });
        return;
      }
      greetingAdapter.addOne(state, newGreeting);
    });

    // maybe add a new greeting when default language is updated
    builder.addCase(updateDefaultLanguage, (state, action) => {
      const languageCode = action.payload.languageCode;
      const newGreeting: Greeting = {
        ...DEFAULT_GREETING,
        languageCode,
      };
      // update greeting if existing
      const existingGreeting = state.entities[languageCode];
      if (existingGreeting) {
        assignDefinedFields(existingGreeting, newGreeting);
        assignMetadata(action, existingGreeting);
        greetingAdapter.updateOne(state, {
          id: languageCode,
          changes: existingGreeting,
        });
        return;
      }
      greetingAdapter.addOne(state, newGreeting);
    });

    // update greeting references when variables are updated
    builder.addCase(updateVariable, (state, action) => {
      const { id: variableId } = action.payload;
      const greetings = Object.values(state.entities).filter(
        g => g?.references.variables[variableId],
      );
      greetings.forEach(g => {
        greetingReferencesAdapter.removeReference(
          state,
          getAgentSettingsReferenceKey(
            'greeting',
            g.languageCode ?? DEFAULT_AGENT_SETTINGS_LANGUAGE_CODE,
          ),
          'variables',
          variableId,
        );
      });
    });

    // update greeting references when variables are deleted
    builder.addCase(deleteVariable, (state, action) => {
      const { id: variableId } = action.payload;
      const greetings = Object.values(state.entities).filter(
        g => g?.references.variables[variableId],
      );
      greetings.forEach(g => {
        greetingReferencesAdapter.removeReference(
          state,
          getAgentSettingsReferenceKey(
            'greeting',
            g.languageCode ?? DEFAULT_AGENT_SETTINGS_LANGUAGE_CODE,
          ),
          'variables',
          variableId,
        );
      });
    });

    // update greeting when language is removed
    builder.addCase(deleteLanguage, (state, action) => {
      const { code: languageCode } = action.payload;
      if (state.entities[languageCode] !== undefined) {
        greetingAdapter.removeOne(state, languageCode);
      }
    });

    // update greeting references when translations are deleted
    builder.addCase(deleteTranslation, (state, action) => {
      const { id: translationId } = action.payload;
      for (const greeting of Object.values(state.entities)) {
        if (greeting?.references.translations?.[translationId]) {
          greetingReferencesAdapter.removeReference(
            state,
            greeting.languageCode ?? DEFAULT_AGENT_SETTINGS_LANGUAGE_CODE,
            'translations',
            translationId,
          );
        }
      }
    });
  },
});

export default slice.reducer;
